import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useMemo, } from 'react';
import { useCurrentUser } from '../hooks/useCurrentUser.js';
import { useSessionManager } from '../hooks/useSessionManager.js';
const ModulesContext = createContext(null);
/**
 * Holds the modules + instances visible to the current session. Recomputes
 * when the user, organization, or profile changes.
 */
export function ModulesProvider({ resolver, isResolving = false, children, }) {
    const { currentUserId } = useCurrentUser();
    const { currentOrganizationId, currentProfileId } = useSessionManager();
    const value = useMemo(() => {
        const result = resolver({
            currentUserId,
            currentOrganizationId,
            currentProfileId,
        });
        return { modules: result.modules, instances: result.instances, isResolving };
    }, [resolver, currentUserId, currentOrganizationId, currentProfileId, isResolving]);
    return (_jsx(ModulesContext.Provider, { value: value, children: children }));
}
function useModulesContext() {
    const ctx = useContext(ModulesContext);
    if (!ctx) {
        throw new Error('useModules* hooks must be used within a <ModulesProvider>.');
    }
    return ctx;
}
/**
 * True while module access is being resolved (profile tree query in flight).
 * Consumers should show a loader instead of `ForbiddenPage` when this is true.
 */
export function useModulesResolving() {
    return useModulesContext().isResolving;
}
/**
 * Returns module *definitions* (the public catalog). The generic recovers an
 * app-extended type when modules carry app-specific fields like icons.
 */
export function useAvailableModules() {
    return useModulesContext().modules;
}
/**
 * Returns the user-scoped instances visible to the current session. Pass a
 * `slug` to filter to a single module's instances.
 */
export function useAvailableInstances(slug) {
    const { instances } = useModulesContext();
    return useMemo(() => {
        if (!slug)
            return instances;
        return instances.filter((instance) => instance.slug === slug);
    }, [instances, slug]);
}
/**
 * Access check by slug. `hasAccess` is true when the user has at least one
 * instance of the module. Reads from the `ModulesProvider` — no duplicate
 * data-access query.
 */
export function useModuleAccess(slug) {
    const { instances } = useModulesContext();
    return useMemo(() => {
        if (!slug)
            return { hasAccess: false, instances: [] };
        const matches = instances.filter((instance) => instance.slug === slug);
        return { hasAccess: matches.length > 0, instances: matches };
    }, [instances, slug]);
}
/**
 * Resolves a `(slug, mid?)` URL pair to a single `ModuleInstance`.
 *
 * Consumers branch on:
 *   - `candidates.length === 0`  → render 403
 *   - `needsDisambiguation`      → render the instance picker
 *   - `instance` truthy           → render the module page with the instance
 */
export function useModuleInstance(slug, mid) {
    const { instances } = useModulesContext();
    return useMemo(() => {
        if (!slug) {
            return { instance: null, candidates: [], needsDisambiguation: false };
        }
        const candidates = instances.filter((instance) => instance.slug === slug);
        if (mid) {
            const matched = candidates.find((c) => c.id === mid);
            if (matched) {
                return { instance: matched, candidates, needsDisambiguation: false };
            }
            // mid is invalid for this user — fall through to the no-mid rules below.
        }
        if (candidates.length === 1) {
            return { instance: candidates[0], candidates, needsDisambiguation: false };
        }
        if (candidates.length > 1) {
            return { instance: null, candidates, needsDisambiguation: true };
        }
        return { instance: null, candidates, needsDisambiguation: false };
    }, [instances, slug, mid]);
}
//# sourceMappingURL=ModulesContext.js.map